import merge from 'lodash/merge'
import { ChartCandle } from '../chart.typings'
import BaseChartSeries from './baseSeries'
import { DeepPartial } from 'lightweight-charts'
import { defaultKagiOptions, KagiSeriesOptions } from '../customSeries/kagiSeries/options'
import { CustomKagiSeries } from '../customSeries/kagiSeries/kagi-series'
import { atr } from '../../../util/IndicatorUtils/core/indicators'
import { KagiData } from '../customSeries/kagiSeries/data'
import { getTimeWidthInSeconds } from '../chartHelpers'
import { istToUTC, utcToIST } from '../../../util/dateTime'

class KagiSeries extends BaseChartSeries<KagiData, KagiSeriesOptions> {
    customPaneView?: CustomKagiSeries<KagiData>
    lastCandleTime?: number

    init() {
            this.customPaneView = new CustomKagiSeries();
        }
    addSeriesToChart() {
        return this.chart.chartApi.addCustomSeries(this.customPaneView!, this.seriesOptions)
    }

    getDefaultOptions(): KagiSeriesOptions {
        return merge(defaultKagiOptions, this.options)
    }

    getPrimaryData(length = 0): ChartCandle[] {
        return length > 0 ? this.data.slice(-length) : this.data;
    }

    onOptionsUpdate(options: DeepPartial<KagiSeriesOptions>): void {
        this._setData(this.rawData);
        let update = (["boxMethod", "atrLength", "percentageLTP", "reversalAmount"] as (keyof KagiSeriesOptions)[]).reduce((result, key) => {
            return result || (key in options);
        }, false);

        if (update) {
            this.chart.refreshNonPrimaryGroups();
            this.chart.checkIfDataRequired();
        }
    }

    generateSeriesData(data: ChartCandle[], metadata: Record<string, any> = {}, update = false): KagiData[] {
        const rawData = this.rawData;
        if (rawData.length === 0) return []
        if (rawData.length === 1) return rawData.map(v => ({...v, ltp: 0}));
        const candles: KagiData[] = update ? this.data : [];
        const threshold = this.computeReversalThreshold(rawData);
        let startIndex = 2;
        const lastKagiCandle = this.data.pop();
        if (update && lastKagiCandle) {
            for (let c=rawData.length - 1; c >=0; c--) {
                let candle = rawData[c];
                if (c && candle.time === lastKagiCandle.time) {
                    startIndex = c+1;
                    break;
                }
            }
        }
        let currentBase = rawData[startIndex-2].close;
        let currentCandle = rawData[startIndex - 1];
        let incompleteKagi: KagiData = {
            ...currentCandle,
            open: currentBase,
            close: currentCandle.close,
            high: Math.max(currentBase, currentCandle.close),
            low: Math.min(currentBase, currentCandle.close),
            ltp: 0,
            volume: currentCandle.volume
        }

        candles.push(incompleteKagi);
        
        

        // let previousKagi: KagiData = lastCompleteKagiCandle;

        const lastCandle  = rawData[rawData.length - 1];

        const timeWidth = getTimeWidthInSeconds(this.chart.chartConfig.timeBucket)
        const lastCandleEndTime = lastCandle ? lastCandle.time as number + timeWidth : 0;
        const lastCandleComplete = lastCandleEndTime <= utcToIST(Date.now()/1000);

        for (let i=startIndex; i<rawData.length; i++) {
            currentCandle = rawData[i];
            const prevUp = incompleteKagi.close >= incompleteKagi.open
            if ((prevUp && currentCandle.close >= incompleteKagi.close) || (!prevUp && currentCandle.close <= incompleteKagi.close)) {
                incompleteKagi.volume += currentCandle.volume;
                if (i === rawData.length - 1 && !lastCandleComplete) {
                    incompleteKagi.ltp = currentCandle.close;
                } else {
                    incompleteKagi.close = currentCandle.close;
                    incompleteKagi.ltp = 0;
                }
                incompleteKagi.nextCandle = undefined;
            } else if (Math.abs(incompleteKagi.close - currentCandle.close) >= threshold) {

                const temp = i === rawData.length - 1 && !lastCandleComplete;
                const newKagi: KagiData = {
                    ...currentCandle,
                    open: incompleteKagi.close,
                    close: currentCandle.close,
                    high: Math.max(incompleteKagi.close, currentCandle.close),
                    low: Math.min(incompleteKagi.close, currentCandle.close),
                    ltp: temp ? currentCandle.close : 0,
                    volume: currentCandle.volume
                }
                if (temp) {
                    incompleteKagi.nextCandle = newKagi;
                } else {
                    incompleteKagi = {...newKagi}
                    candles.push(incompleteKagi);
                }
            } else {
                incompleteKagi.ltp = currentCandle.close;
                incompleteKagi.volume += currentCandle.volume;
                // if (previousKagi) previousKagi.nextCandle = {...currentKagi};
            }
        }
        return candles;
    }

    getKagiCandles(candles: KagiData[], data: ChartCandle[], threshold: number) {
        let startIndex = 0;
        let currentBase = data[0].close;
        let currentCandle = data[1];
        let currentKagi: KagiData = {
            ...data[1],
            open: currentBase,
            close: currentCandle.close,
            high: Math.max(currentBase, currentCandle.close),
            low: Math.min(currentBase, currentCandle.close),
            ltp: 0,
            volume: currentCandle.volume
        }
        candles.push(currentKagi);
    }

    computeReversalThreshold(data: ChartCandle[]): number {
        if (data.length === 0) return 0;

        const [lastCandle] = data.slice(-1);
        const ltp = lastCandle.close
        const { boxMethod, reversalAmount, atrLength, percentageLTP,  } = this.seriesOptions;
        switch (boxMethod) {
            case 'traditional':
                return reversalAmount || 1;
            case 'percentage':
                if (percentageLTP === undefined) {
                    throw new Error('percentageLTP must be defined when boxMethod is "percentage"');
                }
                return Number((ltp*percentageLTP/100).toFixed(4));
            case 'atr':
                if (data.length < atrLength) return 0;

                const high: number[] = [], low: number[] = [], close: number[] = [];
                const slice = data.slice(-atrLength);
                slice.forEach(candle => {
                    high.push(candle.high);
                    low.push(candle.low);
                    close.push(candle.close)
                })

                const atrData = atr(high, low, close, atrLength)
                return Number((atrData.length ? atrData[atrData.length - 1] : 0).toFixed(4))
        }
    }
}

export default KagiSeries
